import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Badge,
    Button,
    Group,
    Modal,
    MultiSelect,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
    createFeatureFlag,
    deleteFeatureFlag,
    listFeatureFlags,
    listOrganizations,
    updateFeatureFlag,
} from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { PageHeader } from '../../components/PageHeader';
import { RemoveEntityAction } from '../../components/RemoveEntityAction';
import type { FeatureFlag } from '../../types/api';

type FlagFormValues = {
    key: string;
    enabled: boolean;
    organizationIds: string[];
    userIds: string;
    description: string;
};

function scopeLabel(
    flag: FeatureFlag,
    orgNameById: Map<string, string>,
    globalLabel: string,
): string {
    const ids = flag.organizationIds ?? [];
    if (ids.length === 0) return globalLabel;
    return ids.map((id) => orgNameById.get(id) ?? id).join(', ');
}

export function FeatureFlagsPage() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<FeatureFlag | null>(null);

    const { data: flags, isLoading } = useQuery({
        queryKey: ['feature-flags'],
        queryFn: listFeatureFlags,
    });

    // Org options for the scope MultiSelect. Fetched once, high limit — the org
    // count is small. An empty selection means "all organizations" (global).
    const { data: orgPage } = useQuery({
        queryKey: ['organizations', 'all-for-flags'],
        queryFn: () => listOrganizations({ page: 1, limit: 1000 }),
    });
    const orgOptions = useMemo(
        () =>
            (orgPage?.data ?? []).map((org) => ({
                value: org.id,
                label: org.name,
            })),
        [orgPage],
    );
    const orgNameById = useMemo(
        () => new Map((orgPage?.data ?? []).map((org) => [org.id, org.name])),
        [orgPage],
    );

    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
        // Capabilities surface per-org flags; invalidate so clients refetch.
        void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
    };

    const toggleMutation = useMutation({
        mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
            updateFeatureFlag(id, { enabled }),
        onSuccess: () => invalidate(),
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const createForm = useForm<FlagFormValues>({
        initialValues: {
            key: '',
            enabled: true,
            organizationIds: [],
            userIds: '',
            description: '',
        },
    });

    const editForm = useForm<FlagFormValues>({
        initialValues: {
            key: '',
            enabled: false,
            organizationIds: [],
            userIds: '',
            description: '',
        },
    });

    const createMutation = useMutation({
        mutationFn: () => {
            const v = createForm.values;
            const userIds = v.userIds
                .split(/[,\s]+/)
                .map((s) => s.trim())
                .filter(Boolean);
            return createFeatureFlag({
                key: v.key.trim(),
                enabled: v.enabled,
                organizationIds: v.organizationIds,
                userIds,
                description: v.description.trim() || undefined,
            });
        },
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.flags.created') });
            setCreateOpen(false);
            createForm.reset();
            invalidate();
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const updateMutation = useMutation({
        mutationFn: (id: string) => {
            const v = editForm.values;
            const userIds = v.userIds
                .split(/[,\s]+/)
                .map((s) => s.trim())
                .filter(Boolean);
            return updateFeatureFlag(id, {
                key: v.key.trim(),
                enabled: v.enabled,
                organizationIds: v.organizationIds,
                userIds,
                description: v.description.trim() || undefined,
            });
        },
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.flags.updated') });
            setEditTarget(null);
            invalidate();
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const openEdit = (flag: FeatureFlag) => {
        editForm.setValues({
            key: flag.key,
            enabled: flag.enabled,
            organizationIds: flag.organizationIds ?? [],
            userIds: (flag.userIds ?? []).join(', '),
            description: flag.description ?? '',
        });
        setEditTarget(flag);
    };

    const rows = flags?.data ?? [];

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.flags.title')}
                description={t('features.flags.description')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        {t('features.flags.createAction')}
                    </Button>
                }
            />
            <DataPanel>
                <Table withTableBorder highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{t('features.flags.colKey')}</Table.Th>
                            <Table.Th>{t('features.flags.colScope')}</Table.Th>
                            <Table.Th>{t('features.flags.colStatus')}</Table.Th>
                            <Table.Th>{t('features.flags.colAllowlist')}</Table.Th>
                            <Table.Th>{t('features.flags.colDescription')}</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.map((flag) => (
                            <Table.Tr key={flag.id}>
                                <Table.Td>
                                    <Text fw={600}>{flag.key}</Text>
                                </Table.Td>
                                <Table.Td>{scopeLabel(flag, orgNameById, t('features.flags.scopeGlobal'))}</Table.Td>
                                <Table.Td>
                                    <Switch
                                        checked={flag.enabled}
                                        onChange={(e) =>
                                            toggleMutation.mutate({
                                                id: flag.id,
                                                enabled: e.currentTarget.checked,
                                            })
                                        }
                                        aria-label={t('features.flags.toggleAria', {
                                            key: flag.key,
                                        })}
                                    />
                                </Table.Td>
                                <Table.Td>
                                    {flag.userIds && flag.userIds.length > 0 ? (
                                        <Badge variant="light">
                                            {t('features.flags.allowlistCount', {
                                                count: flag.userIds.length,
                                            })}
                                        </Badge>
                                    ) : (
                                        <Text size="sm" c="dimmed">
                                            {t('features.flags.allowlistAll')}
                                        </Text>
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm" lineClamp={2}>
                                        {flag.description ?? t('common.empty')}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Group gap="xs" wrap="nowrap">
                                        <Button
                                            variant="light"
                                            size="xs"
                                            onClick={() => openEdit(flag)}
                                        >
                                            {t('common.actions.edit')}
                                        </Button>
                                        <RemoveEntityAction
                                            entityId={flag.id}
                                            entityName={flag.key}
                                            deleteFn={deleteFeatureFlag}
                                            queryKeys={['feature-flags', 'capabilities']}
                                            successMessageKey="features.flags.removed"
                                        />
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                        {!isLoading && rows.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={6}>
                                    <Text c="dimmed" ta="center" py="md">
                                        {t('features.flags.empty')}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ) : null}
                    </Table.Tbody>
                </Table>
            </DataPanel>

            <Modal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                title={t('features.flags.createModalTitle')}
            >
                <Stack>
                    <TextInput
                        label={t('features.flags.fieldKey')}
                        required
                        {...createForm.getInputProps('key')}
                    />
                    <MultiSelect
                        label={t('features.flags.fieldScope')}
                        description={t('features.flags.scopeEmptyGlobalHint')}
                        placeholder={t('features.flags.scopeGlobal')}
                        clearable
                        searchable
                        data={orgOptions}
                        {...createForm.getInputProps('organizationIds')}
                    />
                    <Switch
                        label={t('features.flags.fieldEnabled')}
                        {...createForm.getInputProps('enabled', { type: 'checkbox' })}
                    />
                    <Textarea
                        label={t('features.flags.fieldAllowlist')}
                        description={t('features.flags.fieldAllowlistHint')}
                        placeholder="uuid, uuid, …"
                        {...createForm.getInputProps('userIds')}
                    />
                    <TextInput
                        label={t('features.flags.fieldDescription')}
                        {...createForm.getInputProps('description')}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setCreateOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button
                            loading={createMutation.isPending}
                            onClick={() => createMutation.mutate()}
                        >
                            {t('common.actions.create')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={Boolean(editTarget)}
                onClose={() => setEditTarget(null)}
                title={t('features.flags.editModalTitle')}
            >
                <Stack>
                    <TextInput
                        label={t('features.flags.fieldKey')}
                        required
                        {...editForm.getInputProps('key')}
                    />
                    <MultiSelect
                        label={t('features.flags.fieldScope')}
                        description={t('features.flags.scopeEmptyGlobalHint')}
                        placeholder={t('features.flags.scopeGlobal')}
                        clearable
                        searchable
                        data={orgOptions}
                        {...editForm.getInputProps('organizationIds')}
                    />
                    <Switch
                        label={t('features.flags.fieldEnabled')}
                        {...editForm.getInputProps('enabled', { type: 'checkbox' })}
                    />
                    <Textarea
                        label={t('features.flags.fieldAllowlist')}
                        description={t('features.flags.fieldAllowlistHint')}
                        placeholder="uuid, uuid, …"
                        {...editForm.getInputProps('userIds')}
                    />
                    <TextInput
                        label={t('features.flags.fieldDescription')}
                        {...editForm.getInputProps('description')}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setEditTarget(null)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button
                            loading={updateMutation.isPending}
                            onClick={() => editTarget && updateMutation.mutate(editTarget.id)}
                        >
                            {t('common.actions.save')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}